/**
 * V2 Switch — Radix Switch themed to V2 tokens, faithful to the
 * `.switch` rule in design_reference/styles/app.css (accent fill when on,
 * accent-tx thumb).
 */

import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../../../lib/utils';

/** V2 toggle switch. */
export const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitive.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      'relative inline-flex w-[34px] h-[19px] shrink-0 items-center rounded-full transition-colors duration-150 outline-none',
      'bg-[var(--bg-4)] border border-[var(--bd)]',
      'focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]',
      'data-[state=checked]:bg-[var(--accent)] data-[state=checked]:border-[color-mix(in_oklab,var(--accent)_60%,black)]',
      'disabled:opacity-50 disabled:pointer-events-none',
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        'block w-[14px] h-[14px] rounded-full translate-x-[1.5px] transition-transform duration-150',
        'bg-[var(--tx-mid)]',
        'data-[state=checked]:translate-x-[16.5px] data-[state=checked]:bg-[var(--accent-tx)]',
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;
